import { Injectable, computed, inject } from '@angular/core';
import { ALL_SETUP_TASKS, SetupTask } from '../config/setup-tasks.config';
import { CompanyService } from './company.service';
import { VoucherProductService } from './voucher-product.service';

/** Visual tone of the current setup phase, used to pick colors and copy. */
export type SetupTone = 'required' | 'optional' | 'done';

export interface SetupStatus {
  tone: SetupTone;
  title: string;
  subtitle: string;
  /** 0-100, describes the progress bar of the current phase. */
  percent: number;
}

/**
 * Derives the onboarding checklist state from company + product data.
 *
 * Lives in a service so that the navbar bell, the mobile menu and any future
 * dashboard widget all read the same computed state instead of recomputing it.
 */
@Injectable({ providedIn: 'root' })
export class SetupProgressService {
  private readonly companyService = inject(CompanyService);
  private readonly productService = inject(VoucherProductService);

  // Reads the stores' signals directly — these used to bridge back through the
  // compatibility observables, which added a toSignal layer for no reason.
  readonly company = this.companyService.company;

  private readonly productCount = computed(() => this.productService.products().length);

  /** All tasks that apply to this company, regardless of completion. */
  private readonly visibleTasks = computed<SetupTask[]>(() => {
    const company = this.company();
    if (!company) return [];
    return ALL_SETUP_TASKS.filter((task) => !task.isVisible || task.isVisible(company));
  });

  private readonly dismissed = computed(() => this.company()?.dismissed_setup_tasks ?? []);

  /** Open tasks: neither completed, nor dismissed by the user. */
  readonly activeTasks = computed(() => {
    const dismissed = this.dismissed();
    return this.visibleTasks().filter(
      (task) =>
        !dismissed.includes(task.id) &&
        !dismissed.includes(`done:${task.id}`) &&
        !task.isCompleted(this.company()!, this.productCount()),
    );
  });

  readonly requiredTasks = computed(() => this.visibleTasks().filter((task) => task.required));

  readonly activeRequiredTasks = computed(() =>
    this.requiredTasks().filter((task) => !this.isDone(task)),
  );

  /** True while any required task is still open — the shop cannot sell yet. */
  readonly requiredPhase = computed(() => this.activeRequiredTasks().length > 0);

  readonly pendingCount = computed(() => this.activeTasks().length);

  /**
   * The tasks of the current phase — required ones block the start, so they are
   * shown alone until they are done. Mirrors `currentTasks` in the checklist, so
   * the popover can never offer a task the checklist wouldn't list.
   */
  readonly openTasks = computed<SetupTask[]>(() =>
    this.requiredPhase() ? this.activeRequiredTasks() : this.activeTasks(),
  );

  readonly requiredProgressPercent = computed(() => {
    const total = this.requiredTasks().length;
    if (!total) return 100;
    const done = this.requiredTasks().filter((task) => this.isDone(task)).length;
    return Math.round((done / total) * 100);
  });

  readonly progressPercent = computed(() => {
    const visible = this.visibleTasks();
    if (!visible.length) return 0;
    const dismissed = this.dismissed();
    const done = visible.filter(
      (task) =>
        task.isCompleted(this.company()!, this.productCount()) ||
        dismissed.includes(`done:${task.id}`) ||
        dismissed.includes(task.id),
    ).length;
    return Math.round((done / visible.length) * 100);
  });

  /** Single source of truth for the checklist headline, color and bar. */
  readonly status = computed<SetupStatus>(() => {
    if (this.requiredPhase()) {
      const open = this.activeRequiredTasks().length;
      return {
        tone: 'required',
        title: 'Fast startklar!',
        subtitle:
          open === 1 ? 'Nur noch 1 Schritt bis zum Start' : `Noch ${open} Schritte bis zum Start`,
        percent: this.requiredProgressPercent(),
      };
    }

    const pending = this.pendingCount();
    if (pending > 0) {
      return {
        tone: 'optional',
        // A network member doesn't sell, it redeems the network's vouchers.
        title: this.company()?.is_member ? 'Du kannst einlösen!' : 'Du kannst verkaufen!',
        subtitle: `${pending} optionale ${pending === 1 ? 'Verbesserung' : 'Verbesserungen'}`,
        percent: this.progressPercent(),
      };
    }

    return {
      tone: 'done',
      title: 'Einrichtungs-Checkliste',
      subtitle: 'Alles erledigt!',
      percent: 100,
    };
  });

  private isDone(task: SetupTask): boolean {
    const company = this.company();
    if (!company) return false;
    return (
      task.isCompleted(company, this.productCount()) || this.dismissed().includes(`done:${task.id}`)
    );
  }
}
